<template>
  <div class="component-numeric-pagination">
    <ul v-if="isBiggerThanOnePage">
      <NumericPaginationItem
        v-if="isBiggerThanBreakpoint && !isBeginningOfPagination"
        aria-label="Previous Page, Button"
        class="chevron-previous"
        :current-page="_currentPage"
        :page-number="previousPageNumber"
        @item-click="onChevronPreviousClick"
      >
        <span :class="`chevron-${chevronSize}-left-black`" />
      </NumericPaginationItem>

      <NumericPaginationItem
        :current-page="_currentPage"
        :page-number="1"
        @item-click="onPageNumberClick"
      />

      <span
        v-if="showEllipsisBefore"
        aria-label="Ellipsis, More pages"
        class="margin-right-xxs"
      >...</span>

      <NumericPaginationItem
        v-for="pageNumber in middleButtons"
        :key="`page_number_middle_${pageNumber}`"
        :current-page="_currentPage"
        :page-number="pageNumber"
        @item-click="onPageNumberClick"
      />

      <span
        v-if="showEllipsisAfter"
        aria-label="Ellipsis, More pages"
        class="margin-right-xxs"
      >...</span>

      <NumericPaginationItem
        v-if="isBiggerThanOnePage"
        :current-page="_currentPage"
        :page-number="pagesQuantity"
        @item-click="onPageNumberClick"
      />

      <NumericPaginationItem
        v-if="isBiggerThanBreakpoint && !isEndOfPagination"
        aria-label="Next Page, Button"
        class="chevron-next"
        :current-page="_currentPage"
        :page-number="nextPageNumber"
        @item-click="onChevronNextClick"
      >
        <span :class="`chevron-${chevronSize}-right-black`" />
      </NumericPaginationItem>
    </ul>
    <div
      v-else
      class="pagination-total"
    >
      Showing All {{ total }} Items
    </div>
  </div>
</template>

<script setup>
/**
 * There is a readme MD file in the component folder with an image
 * which can be used as reference to understand the logic applied
 * to the components
 *
 */
import {
  computed,
  defineAsyncComponent,
  ref,
  watch,
} from 'vue';

import useEnvParams from '@composables/useEnvParams';

const NumericPaginationItem = defineAsyncComponent(() => import('./NumericPaginationItem'));

/**
 * currentPageInitial = The current page to be used as initial on server side
 * currentPage = The current page, managed/provided by parent component on client side
 * total = The total of items
 */
const props = defineProps({
  currentPageInitial: {
    required: true,
    type: [Number, String],
  },
  currentPage: {
    required: true,
    type: [Number, String],
  },
  total: {
    required: true,
    type: Number,
  },
});

const emit = defineEmits([
  'paginationPageNumber',
  'paginationPrevious',
  'paginationNext',
]);

// Internal current page ref
const _currentPage = ref(Number(props.currentPage));

const init = () => {
  const isServer = typeof window === 'undefined';
  /**
     * Set the current page with the initial value when on
     * server side because SortableGrid logic initializes
     * currentPage with 1, even when it is a different value. This
     * avoids server vs. client mismatches and fix the rendered structure.
     */
  /* istanbul ignore else */
  if (isServer && props.currentPageInitial) _currentPage.value = Number(props.currentPageInitial);
};

init();

const defaultPageSize = ref(60);

// Number of pagination items to show on beginning and end of the pagination (not in the middle)
const edgeButtonsQuantity = ref(5);

/**
 * Threshold is always edgeButtonsQuantity minus one
 * because the last edge buttons item already
 * activates the middle of pagination mode
 */
const edgeButtonsThreshold = computed(() => edgeButtonsQuantity.value - 1);

// Number of buttons to show between the ellipsis
const middleButtonsQuantity = ref(3);

// Max number of pagination items before showing the extra elements (arrow buttons, ellipsis)
const pagesBreakpoint = ref(6);

const pagesQuantity = computed(() => Math.ceil(props.total / defaultPageSize.value));

const isBiggerThanOnePage = computed(() => pagesQuantity.value > 1);
const isBiggerThanBreakpoint = computed(() => pagesQuantity.value > pagesBreakpoint.value);
const isSmallerThanEdgeQuantity = computed(() => pagesQuantity.value <= edgeButtonsQuantity.value);
const isBeginningOfPagination = computed(() => _currentPage.value < edgeButtonsQuantity.value);
const isEndOfPagination = computed(() => !isBeginningOfPagination.value
  && _currentPage.value > pagesQuantity.value - edgeButtonsThreshold.value);
const isMiddleOfPagination = computed(() => !isBeginningOfPagination.value && !isEndOfPagination.value);

const nextPageNumber = computed(() => (_currentPage.value + 1 <= pagesQuantity.value ? _currentPage.value + 1 : pagesQuantity.value));
const previousPageNumber = computed(() => (_currentPage.value - 1 >= 1 ? _currentPage.value - 1 : 1));

const showEllipsisBefore = computed(() => {
  /* istanbul ignore else */
  if (
    isBiggerThanBreakpoint.value
        && (isEndOfPagination.value || isMiddleOfPagination.value)
  ) {
    return true;
  }
  return false;
});

const showEllipsisAfter = computed(() => {
  /* istanbul ignore else */
  if (
    isBiggerThanBreakpoint.value
        && (isBeginningOfPagination.value || isMiddleOfPagination.value)
  ) {
    return true;
  }
  return false;
});

const middleButtons = computed(() => {
  let start = 0;
  let range = 0;
  /* istanbul ignore else */
  if (isSmallerThanEdgeQuantity.value) {
    start = 2;
    range = pagesQuantity.value - 2;
  } else if (isMiddleOfPagination.value) {
    const half = Math.floor(middleButtonsQuantity.value / 2);
    range = middleButtonsQuantity.value;
    start = _currentPage.value - half;
  } else if (isEndOfPagination.value) {
    // As the last item is static, subtracts 1 from range
    range = edgeButtonsThreshold.value;
    start = pagesQuantity.value - range;
  } else if (isBeginningOfPagination.value) {
    // As the first item is static, subtracts 1 from range
    range = edgeButtonsThreshold.value;
    // As the first item is static, starts on 2
    start = 2;
  }
  /* istanbul ignore else */
  if (start > 0 && range > 0) return Array.from({ length: range }, (_, i) => start + i);
  return [];
});

watch(() => props.currentPage, (value) => {
  _currentPage.value = Number(value);
});

const onChevronPreviousClick = (pageNumber) => {
  emit('paginationPrevious', defaultPageSize.value, pageNumber);
};

const onPageNumberClick = (pageNumber) => {
  emit('paginationPageNumber', defaultPageSize.value, pageNumber);
};

const onChevronNextClick = (pageNumber) => {
  emit('paginationNext', defaultPageSize.value, pageNumber);
};
const { isMcom } = useEnvParams();
const chevronSize = isMcom ? ref('large') : ref('small');
</script>

<style lang="scss" scoped>
.component-numeric-pagination {
  ul {
    align-items: center;
    display: flex;
    justify-content: center;
    list-style: none;

    :deep(.page-chevron-item) {
        &::after {
            border: none;
        }
    }
  }

  .pagination-total {
    text-align: center;
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
  @import '~@core/csg/src/scss/encodedIcons/mcom/_chevron-large-left-black.scss';
  @import '~@core/csg/src/scss/encodedIcons/mcom/_chevron-large-right-black.scss';

  .numeric-pagination-wrapper {
    margin-bottom: rem-calc(-8);
  }
</style>

<style lang="scss" brand="bcom" scoped>
  @import '~@core/csg/src/scss/encodedIcons/mcom/_chevron-small-right-black.scss';
  @import '~@core/csg/src/scss/encodedIcons/mcom/_chevron-small-left-black.scss';
</style>
